import { useState, type ReactNode } from "react"

import { ErroDaApi, type MudancaDoCadastro } from "../api"
import {
  FormularioDeCampos,
  diferenca,
  faltaNoBloco,
  mensagemDeRecusa,
  recusaDoCampo,
  type CampoDoBloco,
  type GradeDoBloco,
  type ValoresDoBloco,
} from "./BlocoDeCampos"
import { Button } from "./Button"
import { ComDica } from "./ComDica"
import * as Icones from "./Icones"
import { toast } from "./Toast"
import { BOTAO_BRANCO } from "../lib/classes"

type Props = {
  titulo: string
  tituloVisivel?: boolean
  dica?: string
  prefixo: string
  campos: CampoDoBloco[]
  valores: ValoresDoBloco
  aoGravar: (mudanca: MudancaDoCadastro) => Promise<void>
  grade?: GradeDoBloco
  depois?: ReactNode
  mensagemDeSucesso?: string
}

export function SecaoDeCampos({
  titulo,
  tituloVisivel = true,
  dica,
  prefixo,
  campos,
  valores,
  aoGravar,
  grade,
  depois,
  mensagemDeSucesso = `${titulo}: alterações salvas`,
}: Props) {
  const [versao, definirVersao] = useState(0)
  const [rascunho, definirRascunho] = useState<ValoresDoBloco>(valores)
  const [salvando, definirSalvando] = useState(false)
  const [recusas, definirRecusas] = useState<Record<string, string>>({})

  const mudanca = diferenca(campos, valores, rascunho)
  const mudou = Object.keys(mudanca).length > 0
  const invalido = campos.map((campo) => recusaDoCampo(campo, rascunho[campo.campo])).find((r) => r !== null) ?? null
  const falta = faltaNoBloco(campos, rascunho) ?? invalido

  const desfazer = () => {
    definirRascunho(valores)
    definirRecusas({})
    definirVersao((v) => v + 1)
  }

  const salvar = async () => {
    if (!mudou || falta !== null || salvando) return
    definirSalvando(true)
    definirRecusas({})
    try {
      await aoGravar(mudanca)
      toast.success(mensagemDeSucesso)
      definirVersao((v) => v + 1)
    } catch (erro) {
      if (erro instanceof ErroDaApi && erro.campo !== null) definirRecusas({ [erro.campo]: mensagemDeRecusa(erro) })
      toast.error(mensagemDeRecusa(erro))
    } finally {
      definirSalvando(false)
    }
  }

  return (
    <form
      data-slot="secao-de-campos"
      aria-label={titulo}
      className="flex flex-col gap-3"
      onSubmit={(evento) => {
        evento.preventDefault()
        void salvar()
      }}
    >
      {tituloVisivel && (
        <h3 data-slot="titulo-da-secao" className="m-0 flex items-center gap-1.5 text-sm font-semibold text-(--tinta)">
          {titulo}
          {dica !== undefined && <ComDica dica={dica} />}
        </h3>
      )}
      <FormularioDeCampos
        key={versao}
        prefixo={prefixo}
        campos={campos}
        inicial={valores}
        aoMudar={definirRascunho}
        conferir
        grade={grade}
        naGaveta
        recusasDeFora={recusas}
      />
      {depois}
      {mudou && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-(--borda) pt-3">
          <Button type="button" variant="outline" className={BOTAO_BRANCO} disabled={salvando} onClick={desfazer}>
            Desfazer
          </Button>
          <Button type="submit" disabled={salvando || falta !== null} title={falta ?? undefined}>
            <Icones.Salvar />
            {salvando ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      )}
    </form>
  )
}
